import {MonoTypeOperatorFunction} from 'rxjs';
// TODO: use lodash
import {tap} from 'rxjs/operators';
import {nanoid} from 'nanoid';

/** Interpret the given value as an integer. */
export function toInteger(value: any): number {
    return parseInt(`${value}`, 10);
}

/** Return (pretty) string representation of value. */
export function toString(value: any): string {
    return (value !== undefined && value !== null) ? `${value}` : '';
}

/** Return the given value "value" unless its outside of the interval [min, max], in which case min or max is returned,
 *  depending on whether value was too small or too large. */
export function getValueInRange(value: number, max: number, min = 0): number {
    return Math.max(Math.min(value, max), min);
}

/** Return whether the given value is a string. */
export function isString(value: any): boolean {
    return typeof value === 'string' && value !== undefined && value !== null;
}

/** Return whether the given value is UUID-String. */
export function isUUID(value: any): boolean {
    if (!isString(value)) return false;
    let regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return regex.test(value);
}

/** Return whether the given value is not a vali UUID-String. */
export function isNoUUID(value: any): boolean {
    return !isUUID(value);
}

/** Return whether the given value is a number. */
export function isNumber(value: any): boolean {
    return !isNaN(toInteger(value));
}

/** Return whether the given value is defined. */
export function isDefined(value: any): boolean {
    return value !== undefined && value !== null;
}

/** Return all values of the given Javascript object. */
export function valuesOfObject(object: Object) {
    let array = [];
    for (let key in object) array.push((object as any)[key]);
    return array;
}

/** Toggles the value of an item in a Set, returning a new Set instance. */
export function toggleSetItem<T>(set: Set<T>, item: T): Set<T> {
    const next = new Set(set);
    if (next.has(item)) {
        next.delete(item);
    } else {
        next.add(item);
    }
    return next;
}

/** primitive text-selector that will work on older IEs too */
export function selectTextOnPage(domElementId: string) {
    const elem = document.getElementById(domElementId);
    if (!!elem) {
        const doc: any = document; //fool typescript for old-browser compatibility
        if (doc.selection) {
            doc.selection.empty();
            const range = doc.body.createTextRange();
            range.moveToElementText(elem);
            range.select();
        } else if (window.getSelection) {
            const sel = window.getSelection();
            if (!!sel) {
                sel.removeAllRanges();
                const range = document.createRange();
                range.selectNode(elem);
                sel.addRange(range);
            }
        }
    }
}

/** NOT SECURE! Use only for testing/stubbing! */
export function getRandomUUID(): string {
    function s4() {
        return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    }

    return s4() + s4() + '-' + s4() + '-' + s4() + '-' +
        s4() + '-' + s4() + s4() + s4();
}

/**
 * Returns a random id that can be used for ids that don't require global uniqueness.
 */
export function getSmallId(): string {
    return nanoid(8);
}

export function startsWithAny(str: string, prefixes: string[]) {
    for (let prefix of prefixes) {
        if (str.startsWith(prefix)) return true;
    }
    return false;
}

export function produceCanonicalShortURL(url: string): string {
    if (!url) {
        return url;
    }
    let i = url.indexOf('://');
    if (i != -1) {
        /** starts with any protocol, for instance "http://www.example.com" */
        url = url.substring(i + 3);
    }
    i = url.indexOf('www.');
    if (i != -1) {
        /** starts with "www.", for instance "www.example.com" */
        url = url.substring(i + 4);
    }
    i = url.indexOf('?');
    if (i == -1) {
        /** there is no question-mark */
        return url;
    } else {
        /** there is a question-mark, which we then cut off */
        return url.substring(0, i);
    }
}

export function b64EncodeUnicode(str: string) {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
        return String.fromCharCode(('0x' + p1) as any);
    }));
}

export function makeDataTestingIdForString(value: string): string {
    return value.replace(/\W+/g, '-').toLowerCase();
}

/**
 * Returns:
 *  0 if vA equals vB
 *  1 if vA is greater than vB
 *      Examples:
 *          vA=12.3.4 and vB=12.3
 *          vA=12.4 and vB=12.3
 *  -1 if vB is greater than vA
 *      Examples:
 *          vA=12.3 and vB=12.3.4
 *          vA=12.2 and vB=12.4
 */
export function compareVersionNumbers(vA: string, vB: string): number {
    const partsOfA = vA.split('.');
    const partsOfB = vB.split('.');

    for (let i = 0; i < partsOfA.length; ++i) {
        // vA has a longer number than vB starting with the same versions
        if (partsOfB.length === i) {
            return 1;
        }

        const currentValueA = toInteger(partsOfA[i]);
        const currentValueB = toInteger(partsOfB[i]);

        if (currentValueA > currentValueB) {
            return 1;
        }

        if (currentValueA < currentValueB) {
            return -1;
        }
    }

    // vB has a longer number than vA starting with the same versions
    if (partsOfA.length !== partsOfB.length) {
        return -1;
    }

    return 0;
}

export function convertBytesToSuitableUnitsString(bytes: number): string {
    if (!bytes) {
        return '';
    }

    const marker = 1024;
    const decimal = 2;
    const kiloBytes = marker; // One Kilobyte is 1024 bytes
    const megaBytes = marker * marker; // One MB is 1024 KB
    const gigaBytes = marker * marker * marker; // One GB is 1024 MB

    if (bytes < kiloBytes) {
        // Return bytes if less than a KB
        return bytes + ' Bytes';
    } else if (bytes < megaBytes) {
        // Return KB if less than a MB
        return (bytes / kiloBytes).toFixed(decimal) + ' KB';
    } else if (bytes < gigaBytes) {
        // Return MB if less than a GB
        return (bytes / megaBytes).toFixed(decimal) + ' MB';
    } else {
        // Return GB if less than a TB
        return (bytes / gigaBytes).toFixed(decimal) + ' GB';
    }
}

export function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Capitalizes the first letter of each word, e.g. 'manual test' -> 'Manual Test'. */
export function toTitleCase(str: string): string {
    return str.replace(/\b\w/g, char => char.toUpperCase());
}

export function tapLog<T>(msg?: string): MonoTypeOperatorFunction<T> {
    return msg ? tap(x => console.log(msg, x)) : tap(console.log);
}

export function isIPAdress(s: string): boolean {
    return /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(s);
}

/**
 * Extends the number in value to the amount of digits in padding by adding 0s to the left
 * @param value
 * @param padding
 */
export function lpad(value: number, padding: number) {
    const zeroes = new Array(padding + 1).join('0');
    return (zeroes + value).slice(-padding);
}

export function runsOnMac() {
    // platform is deprecated, but according to the mozilla dev docs, our use case is the only one in which this is still the only option to check this
    return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
}

/**
 * Converts a base64 encoded string to a Blob object.
 * @param dataBase64 The data as base64 encoded string.
 * @param mimeType The mime type of the file.
 */
export function base64ToBlob(dataBase64: string, filename: string, mimeType: string): Blob {
    const byteCharacters = atob(dataBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], {type: mimeType});
}

/**
 * Converts a base64 encoded string to a File object.
 * @param dataBase64 The data as base64 encoded string.
 * @param filename The name of the file.
 * @param mimeType The mime type of the file.
 */
export function base64ToFile(dataBase64: string, filename: string, mimeType: string): File {
    return new File([base64ToBlob(dataBase64, filename, mimeType)], filename, {type: mimeType});
}

